import { Spinner } from '@change/ds-core-ui';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import { withPrefetchedData } from 'src/shared/prefetch';

import type { LocalPagePrefetchedDataProps } from 'src/app/pages/local/pageContext';
import { LocalPageContextProvider, prefetchPageContext } from 'src/app/pages/local/pageContext';
import { isLoaded } from 'src/app/shared/utils/async';
import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

import { Layout } from '../layout';

import { LocalErrorHandler } from './ErrorHandler';

export const Local = withPrefetchedData(
	function Local({ prefetchedData }: LocalPagePrefetchedDataProps) {
		const { slug } = useKnownAvailableParams<{ slug: string }>();

		return (
			<LocalPageContextProvider
				slug={slug}
				prefetchedData={prefetchedData}
				errorHandler={LocalErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</LocalPageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.slug!, context),
		pageTitle: ({ prefetchedData, i18n, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { displayName, stateCode, countryCode } = prefetchedData.pageData.pageData;
			const country = i18n.getCountry(countryCode)?.name;
			const state = getUsState(stateCode);
			const location = `${displayName}, ${state || country}`;
			return fromTemplate(`Petitions in ${displayName} - Support Local Causes & Make a Difference | ${location}`);
		},
		pageCanonical: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return `/local/${prefetchedData.pageData.pageData.slug}`;
		},
		pageMetadata: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData) || !isLoaded(prefetchedData.trendingPetitionsPaginatedData)) {
				return {};
			}
			const { displayName } = prefetchedData.pageData.pageData;
			const description = `Discover and support local petitions in ${displayName} on Change.org. Join your community in making a difference by signing petitions that matter to you. Be a part of the change today!`;

			const indexed = !!prefetchedData.trendingPetitionsPaginatedData.trendingPetitionsPaginatedData.totalCount;

			return {
				description,
				...(!indexed && { robots: 'noindex' }),
			};
		},
	},
);
